import { Button, Input, Table, message } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { defaultStatus } from "../../../../../helper/options";
import { serverUrl } from "../../../../../nestserver";

const FeeTable = ({
  postTemplateResponse,
  feeResponse,
  setFeeResponse,
  isFeeTemplate,
  isViewStudentFee,
  fetchStudentFee,
  fetchFeeTemplate,
  academicStartDate,
  academicEndDate,
}) => {
  const [editedValues, setEditedValues] = useState({});
  const [editedStudentFeeValues, setEditedStudentFeeValues] = useState({});
  const [editingStudentFeeRow, setEditingStudentFeeRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleEdit = () => {
    setIsEditMode(true);
    const initialEditedValues = feeResponse?.installment_list.reduce(
      (acc, record) => {
        const pc = Array.isArray(record.payable_components)
          ? record.payable_components[0]
          : record.payable_components;
        acc[record._id] = {
          name: record.name,
          dueDate: record.due_date,
          total_amount: pc?.total_amount,
        };
        return acc;
      },
      {}
    );
    setEditedValues(initialEditedValues);
  };

  const handleUpdate = async () => {
    try {
      if (!feeResponse || !feeResponse?.installment_list) {
        throw new Error("Fee response or installment list is missing");
      }

      const updatedData = feeResponse.installment_list.map((item) => {
        const pc = Array.isArray(item.payable_components)
          ? item.payable_components[0]
          : item.payable_components;
        return {
          name: editedValues[item._id]?.name ?? item.name,
          due_date: editedValues[item._id]?.dueDate ?? item.due_date,
          _id: item._id,
          payable_components: [
            {
              ...(pc || {}),
              total_amount: editedValues[item._id]?.total_amount ?? pc?.total_amount,
            },
          ],
        };
      });

      const response = await axios.patch(
        `${serverUrl}/admin/fee-template/${feeResponse._id}/installments/`,
        { installments: updatedData },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        }
      );
      message.success(
        response?.data?.result?.message || "Fee updated successfully"
      );
      const fetchUpdatedFeeTemplate = async () => {
        try {
          const updatedFee = await fetchFeeTemplate();
          setFeeResponse((prevState) => ({
            ...prevState,
            installment_list: updatedFee?.result?.installment_list,
          }));
        } catch (error) {
          console.error("Error fetching updated fee template:", error);
        }
      };

      fetchUpdatedFeeTemplate();
      setIsEditMode(false);
      setEditedValues({});
    } catch (error) {
      console.error("Error updating fee template:", error);
      message.error(
        error?.response?.data?.error?.errors ||
          "An error occurred while updating the fee template"
      );
      setIsEditMode(false);
      setEditedValues({});
    }
  };

  const handleStudentFeeCancel = (recordId) => {
    setEditingStudentFeeRow(null);
    setEditedStudentFeeValues((prev) => {
      const newValues = { ...prev };
      delete newValues[recordId];
      return newValues;
    });
  };

  const handleStudentFeeEdit = (record) => {
    setEditingStudentFeeRow(record._id);
    const pc = Array.isArray(record.payable_components)
      ? record.payable_components[0]
      : record.payable_components;
    setEditedStudentFeeValues({
      ...editedStudentFeeValues,
      [record._id]: { ...(pc || {}) },
    });
  };

  function disabledDate(current) {
    if (!current) return false;
    let effectiveStart = academicStartDate ? moment(academicStartDate) : null;
    let effectiveEnd = academicEndDate ? moment(academicEndDate) : null;
    if (!effectiveStart) {
      const yearStr = String(feeResponse?.year || "");
      const startYr = parseInt(yearStr.split("-")[0]);
      if (!isNaN(startYr)) {
        effectiveStart = moment(`${startYr}-06-01`);
        if (!effectiveEnd) effectiveEnd = moment(`${startYr + 1}-05-31`);
      }
    }
    if (effectiveStart && current < effectiveStart.startOf("day")) return true;
    if (effectiveEnd && current > effectiveEnd.endOf("day")) return true;
    return false;
  }

  // handle update for name and due_date

  const handleStudentFeeUpdate = async (record) => {
    try {
      const updatedData = [...(feeResponse?.installment_list || [])];
      const updatedItem = {
        payable_components: [editedStudentFeeValues[record._id]],
        _id: record._id,
      };

      const response = await axios.patch(
        `${serverUrl}/admin/student-fee/${feeResponse?._id}/installments/`,
        updatedItem,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        }
      );
      message.success(response?.data?.result?.message);
      const updatedStudentFee = await fetchStudentFee();
      const idx = updatedData.findIndex(item => item._id === record._id);
      if (idx !== -1) {
        updatedData[idx].payable_components[0] =
          updatedStudentFee.result.installment_list.find(item => item._id === record._id).payable_components[0];
      }
      setFeeResponse({ ...feeResponse, installment_list: updatedData });

      // Reset editing state
      setEditingStudentFeeRow(null);
      setEditedStudentFeeValues({});
    } catch (error) {
      setEditingStudentFeeRow(null);
      setEditedStudentFeeValues({});
    }
  };
  // Table Settings

  const getDynamicColumns = (data) => {
    const columns = [
      {
        title: "Name",
        key: "name",
        align: "center",
        render: (record) => {
          if (isEditMode) {
            return (
              <Input
                value={editedValues[record._id]?.name}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    [record._id]: {
                      ...editedValues[record._id],
                      name: e.target.value,
                    },
                  })
                }
              />
            );
          } else {
            return <span>{record?.name}</span>;
          }
        },
      },
      {
        title: "Fee",
        dataIndex: "",
        key: "tution",
        align: "center",
        render: (record) => {
          if (editingStudentFeeRow === record._id) {
            return (
              <Input
                value={editedStudentFeeValues[record._id]?.tution || ""}
                onChange={(e) =>
                  setEditedStudentFeeValues({
                    ...editedStudentFeeValues,
                    [record._id]: {
                      ...editedStudentFeeValues[record._id],
                      tution: e.target.value,
                    },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                type="number"
              />
            );
          } else if (isEditMode) {
            return (
              <Input
                type="number"
                value={editedValues[record._id]?.total_amount || ""}
                onChange={(e) =>
                  setEditedValues({
                    ...editedValues,
                    [record._id]: {
                      ...editedValues[record._id],
                      total_amount: e.target.value,
                    },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
              />
            );
          } else {
            return <span>{record?.payable_components[0]?.total_amount}</span>;
          }
        },
      },
      {
        title: "Total Amount",
        dataIndex: [],
        key: "",
        render: (record) => (
          <div style={{ color: "#616161" }} className=" text-base">
            {record?.payable_components[0]?.total_amount}
          </div>
        ),
      },
      {
        title: "Due Date",
        key: "due_date",
        align: "center",
        render: (record) => {
          if (isEditMode) {
            return (
              <DatePicker
                format={"DD/MM/YYYY"}
                value={
                  editedValues[record._id]?.dueDate
                    ? moment(editedValues[record._id].dueDate)
                    : null
                }
                disabledDate={disabledDate}
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(date) =>
                  setEditedValues({
                    ...editedValues,
                    [record._id]: {
                      ...editedValues[record._id],
                      dueDate: date,
                    },
                  })
                }
              />
            );
          } else {
            return (
              <span>{moment(record?.due_date).format("Do MMM YYYY")}</span>
            );
          }
        },
      },
    ];

    const excludedProperties = ["tution", "other", "total_amount"];
    const payable_components = data?.map((item) => item?.payable_components[0]);
    const filteredData = payable_components.map((obj) =>
      Object.keys(obj || {})
        .filter((key) => !excludedProperties.includes(key))
        .reduce((acc, key) => {
          acc[key] = obj[key];
          return acc;
        }, {})
    );
    if (filteredData && filteredData.length > 0 && filteredData[0]) {
      const firstItem = filteredData[0];
      Object.entries(firstItem).forEach(([key, value]) => {
        const newColumn = {
          title: key,
          key: key,
          align: "center",
          render: (record) => {
            const fieldValue = record.payable_components[0]?.[key];
            const isEditing = editingStudentFeeRow === record._id;
            const initialValue =
              editedStudentFeeValues[record._id]?.[key] || fieldValue;
            return isEditing ? (
              <Input
                value={editedStudentFeeValues[record._id]?.[key]}
                onChange={(e) =>
                  setEditedStudentFeeValues({
                    ...editedStudentFeeValues,
                    [record._id]: {
                      ...editedStudentFeeValues[record._id],
                      [key]: e.target.value,
                    },
                  })
                }
              />
            ) : (
              <div
                key={record?._id}
                style={{ color: "#111111" }}
                className="text-base text-center capitalize"
              >
                {initialValue}
              </div>
            );
          },
        };
        // Exclude utility columns from the main installments table
        // columns.splice(2, 0, newColumn);
      });
    }
    if (isViewStudentFee) {
      columns.push({
        title: "Action",
        key: "action",
        align: "center",
        render: (record) => {
          if (editingStudentFeeRow === record._id) {
            return (
              <>
                <Button
                  type="primary"
                  onClick={() => handleStudentFeeUpdate(record)}
                  style={{ marginBottom: "10px" }}
                >
                  Update
                </Button>
                <Button
                  onClick={() => handleStudentFeeCancel(record._id)}
                  style={{ marginLeft: "8px" }}
                >
                  Cancel
                </Button>
              </>
            );
          } else {
            return (
              <Button
                type="link"
                onClick={() => handleStudentFeeEdit(record)}
              >
                Edit
              </Button>
            );
          }
        },
      });
    }

    return columns;
  };

  const getExtraFeesData = (installmentList) => {
    if (!installmentList || installmentList.length === 0) return [];
    const otherFeeInst = installmentList.find(item => item.name === "Other Fee");
    if (!otherFeeInst) return [];
    const payableComponents = otherFeeInst?.payable_components?.[0] || otherFeeInst?.payable_components || {};
    
    const extraFees = [];
    const excluded = ["tution", "total_amount", "other"];
    
    Object.entries(payableComponents).forEach(([key, value]) => {
      if (!excluded.includes(key) && Number(value) > 0) {
        extraFees.push({
          key,
          name: key.charAt(0).toUpperCase() + key.slice(1),
          amount: Number(value),
        });
      }
    });
    return extraFees;
  };

  const installmentList =
    feeResponse?.installment_list ||
    postTemplateResponse?.data?.result?.data ||
    [];

  const columns = getDynamicColumns(installmentList);
  const extraFeesData = getExtraFeesData(installmentList);

  return (
    <>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md w-full "
        >
          <div className="flex justify-end"></div>
          {isFeeTemplate && feeResponse?.status !== defaultStatus?.applied && (
            <div className="flex justify-end p-4">
              {isEditMode ? (
                <>
                  <Button type="primary" onClick={handleUpdate}>
                    Update
                  </Button>
                  <Button
                    onClick={() => setIsEditMode(false)}
                    style={{ marginLeft: "8px" }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button type="primary" onClick={handleEdit}>
                  Edit
                </Button>
              )}
            </div>
          )}

          <div className="py-5">
            <Table
              columns={columns}
              dataSource={installmentList.filter(item => item.name !== "Other Fee")}
              pagination={false}
              loading={
                feeResponse?.installment_list?.isLoading ||
                postTemplateResponse?.data?.result?.data?.loading
              }
            />
          </div>

          {extraFeesData.length > 0 && (
            <div className="p-6 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
              <h4 className="text-base font-bold mb-4 text-orange-500 flex items-center">
                <span className="mr-2">📋</span> Miscellaneous Fees
              </h4>
              <Table
                columns={[
                  {
                    title: "Extra Fee Component",
                    dataIndex: "name",
                    key: "name",
                    align: "left",
                    className: "font-medium text-gray-700",
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    align: "right",
                    render: (amount) => `₹${Number(amount).toLocaleString()}`,
                    className: "font-semibold text-gray-900",
                  }
                ]}
                dataSource={extraFeesData}
                pagination={false}
                bordered={false}
                size="middle"
                summary={(pageData) => {
                  const total = pageData.reduce((sum, item) => sum + item.amount, 0);
                  return (
                    <Table.Summary.Row className="bg-orange-50/20 font-bold">
                      <Table.Summary.Cell index={0} className="text-gray-800">
                        Total Extra Fees
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right" className="text-orange-600">
                        ₹{total.toLocaleString()}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FeeTable;
